import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt, FaFlask, FaPlus, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PageTemplate from '../../components/Common/PageTemplate';

const BeyazOnlukToreni = () => {
  const images = [
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/resimler/3beyazonlukgiymetoreni1.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/resimler/3beyazonlukgiymetoreni2.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/resimler/3beyazonlukgiymetoreni3.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/resimler/3beyazonlukgiymetoreni4.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/resimler/3beyazonlukgiymetoreni5.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/resimler/3beyazonlukgiymetoreni6.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/resimler/3beyazonlukgiymetoreni7.jpg'
  ];

  const [visibleImages, setVisibleImages] = useState(6);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState({});

  const showMoreImages = () => {
    setVisibleImages(images.length);
  };

  const openModal = useCallback((index) => {
    setCurrentImageIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!modalOpen) return;

      switch(e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [modalOpen, closeModal, nextImage, prevImage]);

  const handleImageLoad = useCallback((index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  }, []);

  useEffect(() => {
    if (modalOpen) {
      const preloadIndexes = [
        (currentImageIndex + 1) % images.length,
        (currentImageIndex - 1 + images.length) % images.length
      ];

      preloadIndexes.forEach(index => {
        const img = new Image();
        img.src = images[index];
      });
    }
  }, [currentImageIndex, modalOpen, images]);

  const ImageModal = () => {
    if (!modalOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
        onClick={closeModal}
      >
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 z-60 text-white hover:text-gray-300 transition-colors"
        >
          <FaTimes size={24} />
        </button>

        <div className="absolute top-4 left-4 z-60 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full text-sm">
          {currentImageIndex + 1} / {images.length}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            prevImage();
          }}
          className="absolute left-4 z-60 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
        >
          <FaChevronLeft size={20} />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            nextImage();
          }}
          className="absolute right-4 z-60 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
        >
          <FaChevronRight size={20} />
        </button>

        <div
          className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={images[currentImageIndex]}
            alt={`Beyaz Önlük Töreni ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            loading="eager"
          />
        </div>

        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-60 text-white bg-black bg-opacity-50 px-4 py-2 rounded-full text-sm">
          Beyaz Önlük Töreni - Fotoğraf {currentImageIndex + 1}
        </div>
      </div>
    );
  };

  return (
    <PageTemplate
      title="Hacettepe Üniversitesi Kimya Bölümü 3. Beyaz Önlük Giyme Töreni Gerçekleştirildi"
      subtitle="Kimya Bölümü öğrencileri beyaz önlüklerini giyerek meslek hayatlarına ilk adımı attı."
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Ana İçerik */}
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl -mt-16 relative z-10 mb-16">
          {/* Etkinlik Bilgileri */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
              <FaCalendarAlt className="text-blue-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">Tarih</p>
                <p className="text-gray-800 font-semibold">3 Ekim 2025</p>
                <p className="text-gray-600 text-sm">Cuma</p>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-100">
              <FaMapMarkerAlt className="text-green-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-600 uppercase tracking-wide">Mekan</p>
                <p className="text-gray-800 font-semibold">Mehmet Akif Ersoy Salonu</p>
                <p className="text-gray-600 text-sm">Hacettepe Üniversitesi</p>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl border border-purple-100">
              <FaFlask className="text-purple-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">Bölüm</p>
                <p className="text-gray-800 font-semibold">Kimya Bölümü</p>
                <p className="text-gray-600 text-sm">3. Beyaz Önlük Töreni</p>
              </div>
            </div>
          </div>

          {/* Ana Metin */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-gradient-to-r from-hacettepe-blue/5 to-hacettepe-red/5 p-8 rounded-2xl border-l-4 border-hacettepe-blue">
              <p className="text-xl leading-relaxed text-gray-700 mb-6">
                <span className="font-semibold text-hacettepe-blue">Hacettepe Üniversitesi Fen Fakültesi Kimya Bölümü</span> tarafından düzenlenen
                <span className="font-semibold"> 3. Beyaz Önlük Giyme Töreni</span>,
                <span className="font-semibold text-hacettepe-red"> 3 Ekim 2025 Cuma günü</span> Mehmet Akif Ersoy Salonu'nda gerçekleştirilmiştir.
              </p>

              <p className="text-lg leading-relaxed text-gray-700 mb-4">
                Törende, öğrenciler öğretim üyeleri eşliğinde beyaz önlüklerini giyerek mesleki yaşamlarına ilk adımlarını atmışlardır.
              </p>

              <p className="text-lg leading-relaxed text-gray-700">
                Açılış konuşmalarında, bilimsel etik ilkeler, mesleki sorumluluk ve akademik bağlılığın önemi vurgulanmıştır.
              </p>
            </div>
          </div>
        </div>

        {/* Fotoğraf Galerisi */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
              🥼 Törenden Kareler
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-hacettepe-blue to-hacettepe-red mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.slice(0, visibleImages).map((src, index) => (
              <div
                key={index}
                className="group relative aspect-square overflow-hidden rounded-2xl shadow-lg bg-gray-200 cursor-pointer transform hover:scale-105 transition-all duration-300"
                onClick={() => openModal(index)}
              >
                {!imageLoaded[index] && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-hacettepe-blue border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}

                <img
                  src={src}
                  alt={`Beyaz Önlük Töreni ${index + 1}`}
                  className={`w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110 ${
                    imageLoaded[index] ? 'opacity-100' : 'opacity-0'
                  }`}
                  loading={index < 6 ? "eager" : "lazy"}
                  onLoad={() => handleImageLoad(index)}
                  onError={() => handleImageLoad(index)}
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

                <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                  <p className="text-sm font-medium">Beyaz Önlük Töreni</p>
                  <p className="text-xs opacity-75">Fotoğraf {index + 1} • Tıklayın</p>
                </div>

                <div className="absolute top-4 right-4 bg-black bg-opacity-50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="w-4 h-4 border-2 border-white rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {visibleImages < images.length && (
            <div className="text-center mt-12">
              <button
                onClick={showMoreImages}
                className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-hacettepe-blue to-blue-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-300"
              >
                <FaPlus className="mr-3 -ml-1 h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                Tüm Fotoğrafları Gör ({images.length - visibleImages} fotoğraf daha)
              </button>
            </div>
          )}
        </div>

        {/* Geri Dön Butonu */}
        <div className="text-center">
          <Link
            to="/haberler"
            className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-gray-800 to-gray-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-gray-300"
          >
            <FaArrowLeft className="mr-3 -ml-1 h-5 w-5 group-hover:-translate-x-1 transition-transform duration-300" />
            Tüm Haberlere Geri Dön
          </Link>
        </div>

        <ImageModal />
      </div>
    </PageTemplate>
  );
};

export default BeyazOnlukToreni;
